<script setup lang="ts">
import { computed, ref, storeToRefs, useForm, watch } from '#imports';
import { toTypedSchema } from '@vee-validate/valibot';
import { object, optional, pipe, string, nonEmpty, email } from 'valibot';
import { errorValidationMessages } from '@/app/messages';

import { userSignService } from './../api';
import { useUserDialogStore } from './../store/dialog';
import { useDialogResponseStore } from '@/widgets/dialog-response/store';
import { CaptchaProcessEnum, useCaptchaStore } from '@/features/captcha/store';

const props = withDefaults(defineProps<{
	modelValue: boolean;
}>(), {
	modelValue: false,
});

const emit = defineEmits<{
	(e: 'update:modelValue'): void;
}>();

const value = computed({
	get: () => props.modelValue,
	set: () => emit('update:modelValue'),
});

const userDialogStore = useUserDialogStore();
const { showSpecialUserDialog } = userDialogStore;

const dialogResponseStore = useDialogResponseStore();
const { setResponseModalMessage, showResponseModal } = dialogResponseStore;

/** Data */
const { defineField, handleSubmit, errors } = useForm({
	validationSchema: toTypedSchema(
		object({
			email: optional(pipe(
				string(),
				nonEmpty(errorValidationMessages.required.email),
				email(),
			), ''),
		}),
	),
});

const [emailModel, emailAttrs] = defineField('email');

/** Handle Submit */
const captchaStore = useCaptchaStore();
const { captchaToken, captchaProcess } = storeToRefs(captchaStore);
const { captchaExecute, captchaReset } = captchaStore;

const payload = ref();

const onSubmit = handleSubmit((model) => {
	payload.value = model;
	captchaExecute(CaptchaProcessEnum.ForgetPassword);
});

watch(captchaToken, async (val) => {
	if (val.length  && captchaProcess.value === CaptchaProcessEnum.ForgetPassword) {
		const data = await userSignService.changePassword({
			...payload.value,
			captcha_token: val,
		});

		captchaReset();

		if ('error' in data) {
			setResponseModalMessage('Ошибка');
		} else {
			setResponseModalMessage(data.detail);
		}

		setTimeout(() => showResponseModal(), 0);

		emit('update:modelValue');
	}
});
</script>

<template>
	<v-dialog v-model="value">
		<div class="dialog-user-forget-password">
			<form class="form" @submit.prevent="onSubmit">
				<div class="form__block">
					<h2 class="dialog-user-forget-password__title">
						Забыли пароль?
					</h2>
				</div>

				<div class="form__block">
					<p>
						Введите адрес электронной почты, чтобы получить ссылку для восстановления пароля.
					</p>
				</div>

				<div class="form__block">
					<div class="form__field">
						<v-input
							v-model="emailModel"
							v-bind="emailAttrs"
							type="email"
							placeholder="Email"
							name="email"
							required
							:is-error="!!errors.email"
							:error-message="errors.email"
						/>
					</div>
				</div>

				<div class="form__block">
					<div class="form__field">
						<v-button
							type="submit"
							class="w-full"
							color="black"
						>
							Восстановить пароль
						</v-button>
					</div>
					<div class="form__field">
						<v-button
							tag="a"
							href="#"
							class="w-full"
							@click.prevent="showSpecialUserDialog('sign')"
						>
							Вернуться к авторизации
						</v-button>
					</div>
				</div>
			</form>
		</div>
	</v-dialog>
</template>
